import { type LabelMedium, labelMediumInput } from "@bt/domain";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Field } from "@/components/app/field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/misc";
import { Switch } from "@/components/ui/switch";
import { useTRPC } from "@/lib/trpc";
import { useShowError } from "./shared";

type Values = Record<
  | "width"
  | "printable"
  | "height"
  | "margin"
  | "columns"
  | "rows"
  | "labelWidth"
  | "labelHeight"
  | "pitchX"
  | "pitchY"
  | "offsetX"
  | "offsetY",
  string
>;

const START: Values = {
  width: "62",
  printable: "58.9",
  height: "297",
  margin: "10",
  columns: "4",
  rows: "10",
  labelWidth: "48.5",
  labelHeight: "25.4",
  pitchX: "48.5",
  pitchY: "25.4",
  offsetX: "8",
  offsetY: "21.5",
};

const number = (value: string) => Number(value.replace(",", "."));

/** The medium the form describes, as the server's schema checks it. */
function mediumOf(kind: "roll" | "sheet", v: Values, template: boolean): LabelMedium {
  if (kind === "roll") return { kind, width: number(v.width), printable: number(v.printable) };
  const margin = number(v.margin);
  return {
    kind,
    width: number(v.width),
    height: number(v.height),
    margins: { top: margin, right: margin, bottom: margin, left: margin },
    template: template
      ? {
          columns: number(v.columns),
          rows: number(v.rows),
          labelWidth: number(v.labelWidth),
          labelHeight: number(v.labelHeight),
          pitchX: number(v.pitchX),
          pitchY: number(v.pitchY),
          offsetX: number(v.offsetX),
          offsetY: number(v.offsetY),
        }
      : null,
  };
}

/**
 * "Custom": a roll (its width and the width the printer prints on) or a sheet (its size and
 * margins, or a label sheet's grid), saved under a name for everyone in the garage.
 */
export function MediumDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (ref: string) => void;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const showError = useShowError();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"roll" | "sheet">("roll");
  const [template, setTemplate] = useState(false);
  const [values, setValues] = useState<Values>(START);
  const save = useMutation({ ...trpc.labels.media.save.mutationOptions(), onError: showError });
  const input = { name, medium: mediumOf(kind, values, template) };
  const parsed = labelMediumInput.safeParse(input);
  const field = (key: keyof Values, label: string) => (
    <Field label={label} htmlFor={`medium-${key}`}>
      <Input
        id={`medium-${key}`}
        inputMode="decimal"
        value={values[key]}
        onChange={(event) => setValues({ ...values, [key]: event.target.value })}
      />
    </Field>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!parsed.success) return;
            save.mutate(parsed.data, {
              onSuccess: (id) => {
                toast.success(t("labels.mediumSaved"));
                onSaved(`custom:${id}`);
                onOpenChange(false);
              },
            });
          }}
        >
          <DialogHeader
            title={t("labels.customMedium")}
            description={t("labels.customMediumBody")}
          />
          <DialogBody className="flex flex-col gap-4">
            <Field label={t("labels.mediumName")} htmlFor="medium-name">
              <Input
                id="medium-name"
                value={name}
                placeholder={t("labels.mediumNameHint")}
                onChange={(event) => setName(event.target.value)}
              />
            </Field>
            <Segmented<"roll" | "sheet">
              aria-label={t("labels.mediumKind")}
              value={kind}
              onChange={(next) => {
                setKind(next);
                setValues({ ...values, width: next === "roll" ? "62" : "210" });
              }}
              options={[
                { value: "roll", label: t("labels.roll") },
                { value: "sheet", label: t("labels.sheet") },
              ]}
            />
            <div className="grid grid-cols-2 gap-3">
              {kind === "roll" ? (
                <>
                  {field("width", t("labels.rollWidth"))}
                  {field("printable", t("labels.printableWidth"))}
                </>
              ) : (
                <>
                  {field("width", t("labels.pageWidth"))}
                  {field("height", t("labels.pageHeight"))}
                  {field("margin", t("labels.margin"))}
                </>
              )}
            </div>
            {kind === "sheet" ? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor="medium-template" className="text-sm font-medium">
                    {t("labels.template")}
                  </label>
                  <Switch id="medium-template" checked={template} onCheckedChange={setTemplate} />
                </div>
                {template ? (
                  <div className="grid grid-cols-2 gap-3">
                    {field("columns", t("labels.columns"))}
                    {field("rows", t("labels.rows"))}
                    {field("labelWidth", t("labels.labelWidth"))}
                    {field("labelHeight", t("labels.labelHeight"))}
                    {field("pitchX", t("labels.pitchX"))}
                    {field("pitchY", t("labels.pitchY"))}
                    {field("offsetX", t("labels.offsetX"))}
                    {field("offsetY", t("labels.offsetY"))}
                  </div>
                ) : null}
              </>
            ) : null}
            <p className="text-xs text-muted-foreground">{t("labels.mmHint")}</p>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={!parsed.success || save.isPending}>
              {t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
